// useTeam.js - نسخه نهایی نهایی
// ============================================================
// رفع کامل مشکلات:
//   ۱. صف سریال per-message
//   ۲. LWW بر اساس timestamp per-user
//   ۳. Debounce
//   ۴. WebSocket delta + merge
//   ۵. Optimistic update با reconcile
//   ۶. defensive کامل
//   ۷. ✅ رفع باگ رفرش: useEffect با [currentUserId] + polling
//   ۸. ✅ String() comparison در همه جا (رفع type mismatch)
//   ۹. ✅ Client-side fallback: اگه سرور empty برگردوند،
//      خودمان همه پیام‌ها/memberها رو fetch و filter می‌کنیم
//  ۱۰. ✅ Cache-first + server fetch در پس‌زمینه
//  ۱۱. ✅ Debug helpers
// ============================================================

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import * as teamService from './teamService';
import { normalizePhone } from '../utils/normalizePhone';
import { getToken } from '../utils/secureStorage';
// ============================================================
// ===== ثابت‌ها =====
// ============================================================
const REACTION_DEBOUNCE_MS = 200;
const WS_MAX_RETRIES = 10;
const WS_BASE_DELAY = 1000;
const WS_MAX_DELAY = 30000;
const CACHE_RETRY_DELAY_MS = 3000;
const EMPTY_RESULT_RETRY_MS = 1500;

// ✅ از دروازه‌بان امن عبور کن
const API_BASE = process.env.REACT_APP_API_URL 
  ? (process.env.REACT_APP_API_URL.startsWith('http') 
      ? process.env.REACT_APP_API_URL 
      : '/api/db')
  : '/api/db';

// ✅ هماهنگ با بقیه اپ
const DEFAULT_AVATAR = '/avatars/avatar34.png';

// ============================================================
// ===== توابع کمکی =====
// ============================================================
const generateId = () => Date.now().toString(36) + Math.random().toString(36).substring(2, 9);

const getBackoffDelay = (attempt) => {
  const delay = Math.min(WS_BASE_DELAY * Math.pow(2, attempt), WS_MAX_DELAY);
  return delay + Math.random() * 1000;
};

const getUserFullNameFromUser = (user) => {
  if (!user) return null;
  if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`.trim();
  if (user.name) return user.name;
  if (user.fullName) return user.fullName;
  if (user.username) return user.username;
  if (user.firstName) return user.firstName;
  return null;
};

const ensureArray = (val) => Array.isArray(val) ? val : [];
const ensureObject = (val) => (val && typeof val === 'object' && !Array.isArray(val)) ? val : {};

const safeJsonParse = (str, fallback) => {
  if (typeof str !== 'string' || !str) return fallback;
  try {
    const parsed = JSON.parse(str);
    return parsed !== null && parsed !== undefined ? parsed : fallback;
  } catch { return fallback; }
};

/**
 * ✅ مقایسه امن ID (string vs number)
 */
const idsEqual = (a, b) => {
  if (a === null || a === undefined || b === null || b === undefined) return false;
  return String(a) === String(b);
};

// ============================================================
// ===== توابع ری‌اکشن =====
// ============================================================
export const normalizeReactions = (raw) => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const norm = {};
  Object.entries(raw).forEach(([key, val]) => {
    if (Array.isArray(val)) {
      val.forEach((uid) => { if (uid) norm[uid] = key; });
    } else if (typeof val === 'string') {
      norm[key] = val;
    } else if (val && typeof val === 'object' && typeof val.emoji === 'string') {
      norm[key] = val.emoji;
    }
  });
  return norm;
};

export const normalizeReactionTs = (raw) => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out = {};
  Object.entries(raw).forEach(([uid, val]) => {
    if (typeof val === 'number' && Number.isFinite(val)) out[uid] = val;
  });
  return out;
};

export const mergeReactionMaps = (rA, tsA, rB, tsB) => {
  const resultR = {};
  const resultTs = {};
  const allUsers = new Set([...Object.keys(rA || {}), ...Object.keys(rB || {})]);

  allUsers.forEach((uid) => {
    const aHas = uid in (rA || {});
    const bHas = uid in (rB || {});
    const aR = aHas ? rA[uid] : undefined;
    const bR = bHas ? rB[uid] : undefined;
    const aT = tsA?.[uid] || 0;
    const bT = tsB?.[uid] || 0;

    let winner, winnerT;
    if (bHas && (!aHas || bT >= aT)) { winner = bR; winnerT = bT; }
    else if (aHas) { winner = aR; winnerT = aT; }
    else return;

    if (winner !== undefined && winner !== null && winner !== '') resultR[uid] = winner;
    resultTs[uid] = winnerT;
  });

  return { reactions: resultR, reactionTs: resultTs };
};

export const getReactionCounts = (reactions) => {
  const norm = normalizeReactions(reactions);
  const counts = {};
  Object.values(norm).forEach((emoji) => {
    if (emoji) counts[emoji] = (counts[emoji] || 0) + 1;
  });
  return counts;
};

export const getUserReaction = (reactions, userId) => {
  const norm = normalizeReactions(reactions);
  return norm[userId] || null;
};

// ============================================================
// ===== Hook اصلی =====
// ============================================================
export const useTeam = (currentUserId, currentUserRole) => {
  // ===== Stateها =====
  const [team, setTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [channelMessages, setChannelMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pinnedMessage, setPinnedMessage] = useState(null);
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [isTyping, setIsTyping] = useState(false);
  const [typingUser, setTypingUser] = useState(null);
  const [messageReactions, setMessageReactions] = useState({});
  const [teamCode, setTeamCode] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [allowStudentMessaging, setAllowStudentMessaging] = useState(true);

  const [presenceMap, setPresenceMap] = useState({}); 

  

  const [channelPage, setChannelPage] = useState(1);
  const [channelTotal, setChannelTotal] = useState(0);

  // 🆕 stateها برای join mode
  const [joinMode, setJoinMode] = useState('invite_only');
  const [representativeCanApproveRequests, setRepresentativeCanApproveRequests] = useState(false);

  // ===== Refs =====
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const isLoadingRef = useRef(false);
  const channelPageRef = useRef(1);
  const typingTimeoutRef = useRef(null);



  const presenceIntervalRef = useRef(null);

  const channelMessagesRef = useRef(channelMessages);
  const reactionQueueRef = useRef({});
  const reactionTimeoutRef = useRef({});
  const teamIdRef = useRef(null);

  const prevUserIdRef = useRef(null);
  const prevRoleRef = useRef(null);
  const cacheRetryTimeoutRef = useRef(null);
  const emptyRetryTimeoutRef = useRef(null);

  // ===== سینک Refs =====
  useEffect(() => { channelMessagesRef.current = channelMessages; }, [channelMessages]);
  useEffect(() => { teamIdRef.current = team?.id || null; }, [team?.id]);

  // 🆕 sync joinMode و representativeCanApproveRequests از team
  useEffect(() => {
    if (!team) return;
    setJoinMode(team.joinMode || 'invite_only');
    setRepresentativeCanApproveRequests(team.representativeCanApproveRequests === true);
  }, [team?.joinMode, team?.representativeCanApproveRequests, team?.id]);

  // ============================================================
  // ===== توابع کمکی ری‌اکشن =====
  // ============================================================
  const applyReactionsToState = useCallback((messageId, reactions, reactionTs) => {
    setChannelMessages((prev) =>
      prev.map((m) => m.id === messageId ? { ...m, reactions, reactionTs } : m)
    );
  }, []);

  const persistReactionsToLocal = useCallback((messageId, reactions, reactionTs) => {
    try {
      let store = ensureObject(safeJsonParse(localStorage.getItem('messageReactions'), {}));
      if (!reactions || Object.keys(reactions).length === 0) delete store[messageId];
      else store[messageId] = reactions;
      localStorage.setItem('messageReactions', JSON.stringify(store));

      let tsStore = ensureObject(safeJsonParse(localStorage.getItem('messageReactionTs'), {}));
      if (!reactionTs || Object.keys(reactionTs).length === 0) delete tsStore[messageId];
      else tsStore[messageId] = reactionTs;
      localStorage.setItem('messageReactionTs', JSON.stringify(tsStore));
    } catch (e) {
      console.warn('⚠️ persistReactionsToLocal failed:', e);
    }
  }, []);

  const hydrateMessagesWithLocalReactions = useCallback((messages) => {
    const safeMessages = ensureArray(messages);
    if (safeMessages.length === 0) return [];

    let localR = ensureObject(safeJsonParse(localStorage.getItem('messageReactions'), {}));
    let localTs = ensureObject(safeJsonParse(localStorage.getItem('messageReactionTs'), {}));

    return safeMessages
      .filter((m) => m && typeof m === 'object' && m.id)
      .map((m) => {
        const serverR = normalizeReactions(m.reactions);
        const serverTs = normalizeReactionTs(m.reactionTs);
        const localMsgR = normalizeReactions(localR[m.id] || {});
        const localMsgTs = normalizeReactionTs(localTs[m.id] || {});
        const merged = mergeReactionMaps(localMsgR, localMsgTs, serverR, serverTs);
        return { ...m, reactions: merged.reactions, reactionTs: merged.reactionTs };
      });
  }, []);

  const sanitizeRawMessages = useCallback((rawMessages) => {
    return ensureArray(rawMessages)
      .filter((msg) => msg && typeof msg === 'object' && msg.id)
      .map((msg) => ({ ...msg, readBy: ensureArray(msg.readBy) }));
  }, []);

  // ============================================================
  // ===== Client-side Fallback: fetch همه پیام‌ها =====
  // ============================================================
  const fetchMessagesFallback = useCallback(async (teamId) => {
    console.log('🔁 Fallback: fetching ALL channelMessages and filtering client-side...');
    try {
      const res = await fetch(`${API_BASE}/channelMessages`);
      if (!res.ok) return { data: [], total: 0 };
      const all = await res.json();
      const allArr = ensureArray(all);
      const filtered = allArr.filter((m) => m && idsEqual(m.teamId, teamId));
      console.log(`✅ Fallback found ${filtered.length} messages for team ${teamId} (out of ${allArr.length} total)`);
      // مرتب‌سازی بر اساس timestamp
      filtered.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      return { data: filtered, total: filtered.length };
    } catch (e) {
      console.warn('⚠️ Fallback fetch failed:', e);
      return { data: [], total: 0 };
    }
  }, []);

  // ============================================================
  // ===== Client-side Fallback: fetch همه اعضا =====
  // ============================================================
  const fetchMembersFallback = useCallback(async (teamId) => {
    console.log('🔁 Fallback: fetching ALL teamMembers and filtering client-side...');
    try {
      const res = await fetch(`${API_BASE}/teamMembers`);
      if (!res.ok) return [];
      const all = await res.json();
      const allArr = ensureArray(all);
      const filtered = allArr.filter((m) => m && idsEqual(m.teamId, teamId));
      console.log(`✅ Fallback found ${filtered.length} members for team ${teamId} (out of ${allArr.length} total)`);
      return filtered;
    } catch (e) {
      console.warn('⚠️ Fallback fetch failed:', e);
      return [];
    }
  }, []);

  // ============================================================
  // ===== دریافت پیام‌ها با fallback =====
  // ============================================================
  const fetchMessagesForTeam = useCallback(async (teamId, page = 1, limit = 50) => {
    let result = { data: [], total: 0 };
    try {
      const res = await teamService.getChannelMessages(teamId, page, limit);
      result = {
        data: ensureArray(res?.data),
        total: typeof res?.total === 'number' ? res.total : ensureArray(res?.data).length,
      };
      console.log(`📨 getChannelMessages(${teamId}, page=${page}) → ${result.data.length} msgs (total=${result.total})`);
    } catch (e) {
      console.warn('⚠️ getChannelMessages failed:', e);
    }

    // ✅ اگه صفر پیام برگشت، از fallback استفاده کن
    if (result.data.length === 0) {
      console.log('⚠️ Zero messages from server → using fallback');
      const fallback = await fetchMessagesFallback(teamId);
      if (fallback.data.length > 0) {
        result = fallback;
      }
    }

    return result;
  }, [fetchMessagesFallback]);

  // ============================================================
  // ===== دریافت اعضا با fallback =====
  // ============================================================
  const fetchMembersForTeam = useCallback(async (teamId) => {
    let members = [];
    try {
      const raw = await teamService.getTeamMembers(teamId);
      members = ensureArray(raw);
      console.log(`👥 getTeamMembers(${teamId}) → ${members.length} members`);
    } catch (e) {
      console.warn('⚠️ getTeamMembers failed:', e);
    }

    if (members.length === 0) {
      console.log('⚠️ Zero members from server → using fallback');
      const fallback = await fetchMembersFallback(teamId);
      if (fallback.length > 0) members = fallback;
    }

    return members;
  }, [fetchMembersFallback]);






  // ============================================================
// ✅ Presence — ارسال heartbeat و آپدیت lastSeen
// ============================================================
const sendPresence = useCallback(() => {
  if (!currentUserId) return;
  const now = Date.now();

  // ۱. آپدیت خودمون در state
  setPresenceMap((prev) => ({ ...prev, [currentUserId]: now }));

  // ۲. آپدیت در localStorage کاربران (کش)
  try {
    const users = ensureArray(safeJsonParse(localStorage.getItem('users'), []));
    const idx = users.findIndex((u) => u && idsEqual(u.id, currentUserId));
    if (idx !== -1) {
      users[idx] = { ...users[idx], lastSeen: now };
    } else {
      users.push({ id: currentUserId, lastSeen: now });
    }
    localStorage.setItem('users', JSON.stringify(users));
  } catch (e) { /* ignore */ }

  // ۳. ✅ جدید: ذخیره‌ی lastSeen در سرور (منبع حقیقت)
  //    کاربران دیگر با خواندن /users این مقدار را می‌بینند

// ... (بقیه کد بالای فایل)

const _token = getToken();
fetch(`${API_BASE}/users/${encodeURIComponent(currentUserId)}`, {
  method: 'PATCH',
  headers: { 
    'Content-Type': 'application/json',
    ...(_token ? { 'Authorization': `Bearer ${_token}` } : {})
  },
  body: JSON.stringify({ lastSeen: now }),
}).catch(() => { /* silent */ });

  // ۴. پخش در WebSocket
  if (wsRef.current?.readyState === WebSocket.OPEN) {
    try {
      wsRef.current.send(JSON.stringify({
        type: 'presence',
        userId: currentUserId,
        teamId: team?.id || null,
        ts: now,
      }));
    } catch (e) { /* ignore */ }
  }
}, [currentUserId, team?.id]);





  // ============================================================
  // ===== بارگذاری دیتا =====
  // ============================================================
  const loadTeamData = useCallback(async () => {
    console.log('========================================');
    console.log('📥 loadTeamData CALLED');
    console.log('  currentUserId:', currentUserId);
    console.log('  currentUserRole:', currentUserRole);
    console.log('  isLoadingRef:', isLoadingRef.current);
    console.log('========================================');

    if (isLoadingRef.current) {
      console.log('⏭️ Already loading, skipping');
      return;
    }
    if (!currentUserId) {
      console.log('⏭️ No currentUserId, skipping (waiting for login)');
      setLoading(false);
      return;
    }

    isLoadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const page = channelPageRef.current;

      // ============================================================
      // ===== معلم =====
      // ============================================================
      if (currentUserRole === 'teacher') {
        let teamData = null;
        let serverResponded = false;

        try {
          teamData = await teamService.getTeamByTeacher(currentUserId);
          serverResponded = true;
          console.log('🏫 getTeamByTeacher →', teamData ? { id: teamData.id, name: teamData.name } : 'null');
        } catch (e) {
          console.warn('⚠️ Server error loading teacher team:', e);
        }

        if (!teamData) {
          const storedTeam = localStorage.getItem('team');
          if (storedTeam) {
            const parsed = safeJsonParse(storedTeam, null);
            if (parsed && idsEqual(parsed.teacherId, currentUserId)) {
              teamData = parsed;
              console.log('📦 Loaded teacher team from localStorage:', parsed.id);
            }
          }
        }

        if (teamData) {
          if (teamData.allowStudentMessaging === undefined) teamData.allowStudentMessaging = true;
          setTeam(teamData);
          setAllowStudentMessaging(teamData.allowStudentMessaging);
          setTeamCode(teamData.teamCode);
          if (teamData.teamCode) localStorage.setItem('teamCode', teamData.teamCode);
          localStorage.setItem('team', JSON.stringify(teamData));

          // ===== اعضا (با fallback) =====
          const allMembers = await fetchMembersForTeam(teamData.id);

          const rawUsers = await teamService.getAllUsers();
          const allUsers = ensureArray(rawUsers);

          // ✅ کش سراسری برای getUserName / getUserAvatar در TeamDashboard
          if (typeof window !== 'undefined') {
            window.__allUsersCache = allUsers;
          }

const membersWithNames = allMembers
  .filter((m) => m && m.studentId)
  .map((m) => {
    const user = allUsers.find((u) => idsEqual(u.id, m.studentId));
    let fullName = getUserFullNameFromUser(user);
    if (!fullName) fullName = `دانش‌آموز ${m.studentId}`;
    return { ...m, name: fullName, avatar: user?.avatar || DEFAULT_AVATAR };
  });

// 🆕 Migration: pendingهای قدیمی direction ندارند → teacher_invite
membersWithNames.forEach((m) => {
  if (m.status === 'pending' && !m.direction) {
    m.direction = 'teacher_invite';
    fetch(`${API_BASE}/teamMembers/${m.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ direction: 'teacher_invite' }),
    }).catch(() => {});
  }
});

setMembers(membersWithNames.filter((m) => m.status === 'active'));
setPendingRequests(membersWithNames.filter((m) => m.status === 'pending'));
localStorage.setItem('teamMembers', JSON.stringify(allMembers));

          // ===== پیام‌ها (با fallback) =====
          const res = await fetchMessagesForTeam(teamData.id, page, 50);
          const sanitized = sanitizeRawMessages(res.data);
          const hydrated = hydrateMessagesWithLocalReactions(sanitized);

          setChannelMessages(hydrated);
          setChannelTotal(res.total || hydrated.length);
          localStorage.setItem('channelMessages', JSON.stringify(hydrated));

          const pinnedList = hydrated.filter((m) => m.pinned === true);
            setPinnedMessages(pinnedList);
            setPinnedMessage(pinnedList[0] || null);

          // ===== لاگ‌ها =====
          try {
            const rawLogs = await teamService.getActivityLogs(teamData.id);
            setActivityLogs(ensureArray(rawLogs));
          } catch (e) {
            setActivityLogs([]);
          }

          console.log(`✅ Teacher load complete: ${hydrated.length} messages displayed`);

        } else {
          const cachedTeam = safeJsonParse(localStorage.getItem('team'), null);
          const hasValidCache = cachedTeam && idsEqual(cachedTeam.teacherId, currentUserId) && cachedTeam.id;

          if (hasValidCache) {
            console.warn('⚠️ Server empty but valid cache exists → using cache:', cachedTeam.id);
            setTeam(cachedTeam);
            setAllowStudentMessaging(cachedTeam.allowStudentMessaging ?? true);
            setTeamCode(cachedTeam.teamCode || null);

            const cachedMembers = ensureArray(safeJsonParse(localStorage.getItem('teamMembers'), []));
            setMembers(cachedMembers.filter((m) => m.status === 'active'));
            setPendingRequests(cachedMembers.filter((m) => m.status === 'pending'));

            const cachedMsgs = ensureArray(safeJsonParse(localStorage.getItem('channelMessages'), []));
            const hydrated = hydrateMessagesWithLocalReactions(sanitizeRawMessages(cachedMsgs));
            setChannelMessages(hydrated);
            setChannelTotal(hydrated.length);

            if (cacheRetryTimeoutRef.current) clearTimeout(cacheRetryTimeoutRef.current);
            cacheRetryTimeoutRef.current = setTimeout(() => {
              if (!isLoadingRef.current) {
                isLoadingRef.current = false;
                loadTeamData();
              }
            }, CACHE_RETRY_DELAY_MS);
          } else if (serverResponded) {
            console.log('🆕 No team found. Clearing localStorage.');
            setTeam(null); setTeamCode(null); setMembers([]); setPendingRequests([]);
            setChannelMessages([]); setActivityLogs([]); setPinnedMessage(null);
            setAllowStudentMessaging(true);
            localStorage.removeItem('teamCode');
            localStorage.removeItem('team');
            localStorage.removeItem('teamMembers');
            localStorage.removeItem('channelMessages');
          } else {
            setTeam(null); setTeamCode(null); setMembers([]); setPendingRequests([]);
            setChannelMessages([]); setActivityLogs([]); setPinnedMessage(null);
            setAllowStudentMessaging(true);
          }
        }

        setIsInitialized(true);
        setLoading(false);
        return;
      }

      // ============================================================
      // ===== دانش‌آموز =====
      // ============================================================
      if (currentUserRole === 'student') {
        let teamData = null;
        let memberData = null;
        let pendingData = null;

        // مرحله ۱: از localStorage
        const storedTeam = localStorage.getItem('team');
        const storedMembers = localStorage.getItem('teamMembers');

        if (storedTeam && storedMembers) {
          const teamObj = safeJsonParse(storedTeam, null);
          const membersArr = ensureArray(safeJsonParse(storedMembers, []));

          if (teamObj) {
            const activeMember = membersArr.find((m) =>
              m && idsEqual(m.studentId, currentUserId) && idsEqual(m.teamId, teamObj.id) && m.status === 'active'
            );
            if (activeMember) {
              teamData = teamObj;
              memberData = activeMember;
              console.log('📦 Found active member in localStorage');
            } else {
              const pendingMember = membersArr.find((m) =>
                m && idsEqual(m.studentId, currentUserId) && idsEqual(m.teamId, teamObj.id) && m.status === 'pending'
              );
              if (pendingMember) {
                pendingData = { ...pendingMember, teamName: teamObj.name };
                teamData = teamObj;
                console.log('📦 Found pending member in localStorage');
              }
            }
          }
        }

        // مرحله ۲: از سرور (با fallback)
        if (!teamData && !pendingData) {
          console.log('🔍 localStorage empty, fetching from server...');

          let allMembers = [];
          try {
            const rawAllMembers = await teamService.getAllTeamMembers();
            allMembers = ensureArray(rawAllMembers);
            console.log(`👥 getAllTeamMembers → ${allMembers.length} members`);
          } catch (e) {
            console.warn('⚠️ getAllTeamMembers failed:', e);
          }

          // ✅ اگه صفر برگشت، از fallback استفاده کن
          if (allMembers.length === 0) {
            console.log('⚠️ Zero members from server → fallback to raw fetch...');
            try {
              const raw = await fetch(`${API_BASE}/teamMembers`);
              if (raw.ok) {
                const all = await raw.json();
                allMembers = ensureArray(all);
                console.log(`🔁 Raw fetch got ${allMembers.length} total members`);
              }
            } catch (e) {
              console.warn('⚠️ Raw fetch failed:', e);
            }
          }

          const myRequests = allMembers.filter((m) => m && idsEqual(m.studentId, currentUserId));
          console.log(`🔍 Found ${myRequests.length} membership requests for user ${currentUserId}`);

          const activeMember = myRequests.find((m) => m.status === 'active');
          if (activeMember) {
            console.log('✅ Found active membership:', activeMember.teamId);
            teamData = await teamService.getTeamById(activeMember.teamId);
            memberData = activeMember;
          } else {
            const pendingMember = myRequests.find((m) => m.status === 'pending');
            if (pendingMember) {
              console.log('⏳ Found pending membership:', pendingMember.teamId);
              pendingData = { ...pendingMember, teamName: pendingMember.teamName || 'گروه' };
              teamData = await teamService.getTeamById(pendingMember.teamId);
            }
          }
        }

        if (teamData && memberData) {
          if (teamData.allowStudentMessaging === undefined) teamData.allowStudentMessaging = true;
          setTeam(teamData);
          setAllowStudentMessaging(teamData.allowStudentMessaging);
          setTeamCode(teamData.teamCode);
          if (teamData.teamCode) localStorage.setItem('teamCode', teamData.teamCode);
          localStorage.setItem('team', JSON.stringify(teamData));

          // ===== اعضا (با fallback) =====
          const teamMembers = await fetchMembersForTeam(teamData.id);

const rawUsers = await teamService.getAllUsers();
const allUsers = ensureArray(rawUsers);

// ✅ کش سراسری برای getUserAvatar در TeamDashboard (هماهنگ با مسیر معلم)
if (typeof window !== 'undefined') {
  window.__allUsersCache = allUsers;
}

const membersWithNames = teamMembers
  .filter((m) => m && m.studentId)
  .map((m) => {
    const user = allUsers.find((u) => idsEqual(u.id, m.studentId));
    let fullName = getUserFullNameFromUser(user);
    if (!fullName) fullName = `دانش‌آموز ${m.studentId}`;
    return { ...m, name: fullName, avatar: user?.avatar || DEFAULT_AVATAR };
  });

          membersWithNames.forEach((m) => {
            if (m.status === 'pending' && !m.direction) {
              m.direction = 'teacher_invite';
              fetch(`${API_BASE}/teamMembers/${m.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ direction: 'teacher_invite' }),
              }).catch(() => {});
            }
          });

          setMembers(membersWithNames.filter((m) => m.status === 'active'));
          setPendingRequests(membersWithNames.filter((m) => m.status === 'pending'));
          localStorage.setItem('teamMembers', JSON.stringify(teamMembers));

          // ===== پیام‌ها (با fallback) =====
          const res = await fetchMessagesForTeam(teamData.id, page, 50);
          const sanitized = sanitizeRawMessages(res.data);
          const hydrated = hydrateMessagesWithLocalReactions(sanitized);

          setChannelMessages(hydrated);
          setChannelTotal(res.total || hydrated.length);
          localStorage.setItem('channelMessages', JSON.stringify(hydrated));

          const pinnedList = hydrated.filter((m) => m.pinned === true);
setPinnedMessages(pinnedList);
setPinnedMessage(pinnedList[0] || null);

          console.log(`✅ Student load complete: ${hydrated.length} messages displayed`);

        } else if (pendingData) {
          setTeam(null); setTeamCode(null);
          setPendingRequests([pendingData]); setMembers([]); setChannelMessages([]);
          setPinnedMessage(null); setAllowStudentMessaging(true);
          if (teamData) {
            localStorage.setItem('team', JSON.stringify(teamData));
            if (teamData.teamCode) localStorage.setItem('teamCode', teamData.teamCode);
          }
        } else {
          setTeam(null); setTeamCode(null); setPendingRequests([]); setMembers([]);
          setChannelMessages([]); setPinnedMessage(null); setAllowStudentMessaging(true);
          localStorage.removeItem('teamCode');
          localStorage.removeItem('team');
          localStorage.removeItem('teamMembers');
          localStorage.removeItem('channelMessages');
        }

        setIsInitialized(true);
        setLoading(false);
      }
    } catch (err) {
      console.error('❌ Error loading team data:', err);
      setError(err.message);
      setLoading(false);
      setIsInitialized(true);
    } finally {
      isLoadingRef.current = false;
    }
  }, [currentUserId, currentUserRole, hydrateMessagesWithLocalReactions, sanitizeRawMessages, fetchMessagesForTeam, fetchMembersForTeam]);




  // ============================================================
// ✅ Presence — heartbeat هر ۲۰ ثانیه
// ============================================================
useEffect(() => {
  if (!currentUserId) return;

  // ارسال فوری
  sendPresence();

  // ارسال دوره‌ای
  presenceIntervalRef.current = setInterval(sendPresence, 20000);

  return () => {
    if (presenceIntervalRef.current) {
      clearInterval(presenceIntervalRef.current);
      presenceIntervalRef.current = null;
    }
  };
}, [currentUserId, sendPresence]);

// ✅ همگام‌سازی presenceMap از سرور (منبع حقیقت) + localStorage (کش)
useEffect(() => {
  if (!currentUserId) return;
  let cancelled = false;

  const loadPresence = async () => {
    // ۱. اول از localStorage (سریع)
    try {
      const users = ensureArray(safeJsonParse(localStorage.getItem('users'), []));
      const map = {};
      users.forEach((u) => {
        if (u && u.id && u.lastSeen) map[u.id] = u.lastSeen;
      });
      if (!cancelled) setPresenceMap((prev) => ({ ...prev, ...map }));
    } catch (e) { /* ignore */ }

    // ۲. بعد از سرور (کامل‌تر — آخرین مقادیر همه کاربران)
    try {
      const res = await fetch(`${API_BASE}/users`);
      if (!res.ok) return;
      const allUsers = await res.json();
      if (cancelled) return;
      const serverMap = {};
      ensureArray(allUsers).forEach((u) => {
        if (u && u.id && u.lastSeen) serverMap[u.id] = u.lastSeen;
      });
      setPresenceMap((prev) => ({ ...prev, ...serverMap }));

      // ✅ به‌روزرسانی localStorage با مقادیر سرور
      try {
        const existing = ensureArray(safeJsonParse(localStorage.getItem('users'), []));
        const merged = [...existing];
        Object.entries(serverMap).forEach(([uid, ts]) => {
          const idx = merged.findIndex((u) => u && idsEqual(u.id, uid));
          if (idx !== -1) merged[idx] = { ...merged[idx], lastSeen: ts };
        });
        localStorage.setItem('users', JSON.stringify(merged));
      } catch (e) { /* ignore */ }
    } catch (e) { /* silent */ }
  };

  loadPresence();
  return () => { cancelled = true; };
}, [currentUserId]);



  // ============================================================
  // ✅✅✅ رفرش خودکار + polling وقتی currentUserId نیست
  // ============================================================
  const [userIdTick, setUserIdTick] = useState(0);

  useEffect(() => {
    // اگه currentUserId نداریم، هر ۳۰۰ms چک کن از localStorage
    if (currentUserId) return;
    console.log('⏳ currentUserId is undefined, polling localStorage...');

    const interval = setInterval(() => {
      const stored = localStorage.getItem('user');
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          if (parsed?.id) {
            console.log('✅ Found user in localStorage! Triggering re-render...');
            setUserIdTick((t) => t + 1);
          }
        } catch {}
      }
    }, 300);

    return () => clearInterval(interval);
  }, [currentUserId, userIdTick]);

  useEffect(() => {
    if (!currentUserId) {
      setLoading(false);
      return;
    }

    const userChanged =
      prevUserIdRef.current !== null &&
      (!idsEqual(prevUserIdRef.current, currentUserId) || prevRoleRef.current !== currentUserRole);

    if (userChanged) {
      console.log('👤 User changed, resetting state');
      setTeam(null); setMembers([]); setPendingRequests([]);
      setChannelMessages([]); setPinnedMessage(null); setActivityLogs([]);
      setIsInitialized(false);
      isLoadingRef.current = false;
      localStorage.removeItem('team');
      localStorage.removeItem('teamMembers');
      localStorage.removeItem('channelMessages');
      localStorage.removeItem('teamCode');
    }

    prevUserIdRef.current = currentUserId;
    prevRoleRef.current = currentUserRole;

    if (!isInitialized || userChanged) {
      console.log('🚀 Triggering loadTeamData');
      isLoadingRef.current = false;
      loadTeamData();
    }
  }, [currentUserId, currentUserRole, isInitialized, loadTeamData, userIdTick]);

  // ============================================================
  // ✅✅✅ رفرش خودکار وقتی تب فعال می‌شه
  // ============================================================
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && currentUserId) {
        console.log('👁️ Tab visible → refreshing...');
        isLoadingRef.current = false;
        loadTeamData();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [currentUserId, loadTeamData]);

  // ============================================================
  // ===== علامت‌گذاری پیام‌ها به‌عنوان خوانده شده =====
  // ============================================================
  const markMessagesAsRead = useCallback(async (specificIds = null) => {
  if (!team?.id || !currentUserId || channelMessages.length === 0) return;

  let unreadMessages = channelMessages.filter(
    (msg) => !idsEqual(msg.senderId, currentUserId) && !ensureArray(msg.readBy).some((id) => idsEqual(id, currentUserId))
  );

  // ✅ فقط پیام‌های مشخص‌شده رو علامت بزن
  if (Array.isArray(specificIds) && specificIds.length > 0) {
    const idSet = new Set(specificIds.map(String));
    unreadMessages = unreadMessages.filter((m) => idSet.has(String(m.id)));
  }

  if (unreadMessages.length === 0) return;

  const messageIds = unreadMessages.map((m) => m.id);

  setChannelMessages((prev) =>
    prev.map((msg) =>
      messageIds.includes(msg.id)
        ? { ...msg, readBy: [...ensureArray(msg.readBy), currentUserId] }
        : msg
    )
  );

  const stored = ensureArray(safeJsonParse(localStorage.getItem('channelMessages'), []));
  const updated = stored.map((msg) =>
    messageIds.includes(msg.id)
      ? { ...msg, readBy: [...ensureArray(msg.readBy), currentUserId] }
      : msg
  );
  localStorage.setItem('channelMessages', JSON.stringify(updated));

  try {
    for (const msg of unreadMessages) {
      await teamService.updateChannelMessage(msg.id, {
        readBy: [...ensureArray(msg.readBy), currentUserId],
      });
    }
  } catch (e) {
    console.warn('⚠️ Could not update readBy on server:', e);
  }

  if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
    wsRef.current.send(JSON.stringify({
      type: 'messagesRead',
      userId: currentUserId,
      messageIds,
    }));
  }
}, [team?.id, currentUserId, channelMessages]);

  // ============================================================
  // ===== createNewTeam =====
  // ============================================================
  const createNewTeam = useCallback(async (teamName, description = '', image = '') => {
    try {
      setLoading(true); setError(null);
      if (!teamName || teamName.trim() === '') throw new Error('نام گروه الزامی است');

      let finalTeam = null;
      try {
        const created = await teamService.createTeam(currentUserId, teamName, description, image);
        if (created && created.id) finalTeam = created;
      } catch (serverErr) {
        console.warn('⚠️ Server not available, using localStorage only');
      }

      if (!finalTeam) {
        finalTeam = {
          id: generateId(),
          name: teamName.trim(),
          description: description || '',
          image: image || '',
          teacherId: currentUserId,
          teamCode: teamService.generateTeamCode(),
          allowStudentMessaging: true,
          createdAt: new Date().toISOString(),
        };
      }

      localStorage.removeItem('team');
      localStorage.removeItem('teamMembers');
      localStorage.removeItem('pendingRequests');
      localStorage.removeItem('teamCode');
      localStorage.removeItem('channelMessages');

      const teacherMember = {
        id: generateId(),
        teamId: finalTeam.id,
        studentId: currentUserId,
        role: 'admin',
        status: 'active',
        joinedAt: new Date().toISOString(),
        name: 'معلم',
        teamCode: finalTeam.teamCode,
      };

      localStorage.setItem('team', JSON.stringify(finalTeam));
      if (finalTeam.teamCode) localStorage.setItem('teamCode', finalTeam.teamCode);
      localStorage.setItem('teamMembers', JSON.stringify([teacherMember]));

      setTeam(finalTeam);
      setTeamCode(finalTeam.teamCode);
      setAllowStudentMessaging(finalTeam.allowStudentMessaging ?? true);
      setMembers([teacherMember]);
      setPendingRequests([]);
      setChannelMessages([]);
      setLoading(false);

      return finalTeam;
    } catch (err) {
      console.error('❌ Error creating team:', err);
      setError(err.message);
      setLoading(false);
      throw err;
    }
  }, [currentUserId]);

  // ============================================================
  // ===== رفرش =====
  // ============================================================
  const refreshTeam = useCallback(() => {
    isLoadingRef.current = false;
    loadTeamData();
  }, [loadTeamData]);

  // ============================================================
  // ===== بقیه توابع (بدون تغییر نسبت به قبل) =====
  // ============================================================
  const updateTeamInfo = useCallback(async (updates) => {
    if (!team) throw new Error('گروهی وجود ندارد');
    try {
      const updated = { ...team, ...updates };
      setTeam(updated);
      if (updated.allowStudentMessaging !== undefined) setAllowStudentMessaging(updated.allowStudentMessaging);
      localStorage.setItem('team', JSON.stringify(updated));
      if (updated.teamCode) {
        setTeamCode(updated.teamCode);
        localStorage.setItem('teamCode', updated.teamCode);
      }
      try {
        await teamService.updateTeam(team.id, updates);
        await teamService.addActivityLog(team.id, currentUserId, 'update_team', `گروه بروزرسانی شد`);
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'teamUpdated', teamId: team.id, updates }));
        }
      } catch (e) {
        console.warn('⚠️ Server not available');
      }
      return updated;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [team, currentUserId]);

  const addMember = useCallback(async (studentId) => {
    if (!team) throw new Error('گروهی وجود ندارد');
    if (!studentId) throw new Error('شناسه دانش‌آموز الزامی است');
    try {
      const allMembers = ensureArray(safeJsonParse(localStorage.getItem('teamMembers'), []));
      const existing = allMembers.find((m) => m && idsEqual(m.studentId, studentId));

      if (existing) {
        if (existing.status === 'active') throw new Error('این کاربر قبلاً عضو گروه است');
        if (existing.status === 'pending') throw new Error('درخواست قبلاً ارسال شده است');
        if (existing.status === 'rejected') {
          existing.status = 'pending';
          localStorage.setItem('teamMembers', JSON.stringify(allMembers));
          setPendingRequests(allMembers.filter((m) => m.status === 'pending'));
          return { success: true, message: 'درخواست عضویت مجدداً ارسال شد' };
        }
      }

      const newMember = {
        id: generateId(),
        teamId: team.id,
        studentId,
        role: 'member',
        status: 'pending',
        joinedAt: new Date().toISOString(),
        teamCode: team.teamCode,
      };
      allMembers.push(newMember);
      localStorage.setItem('teamMembers', JSON.stringify(allMembers));
      setPendingRequests(allMembers.filter((m) => m.status === 'pending'));

      try {
        await teamService.sendMemberRequest(team.id, studentId);
        await teamService.updateUserProfile(studentId, { teamCode: team.teamCode });
      } catch (e) { console.warn('⚠️ Server not available'); }

      return { success: true, message: 'درخواست عضویت ارسال شد' };
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [team, currentUserId]);

  const removeMember = useCallback(async (studentId) => {
    if (!team) throw new Error('گروهی وجود ندارد');
    if (!studentId) throw new Error('شناسه دانش‌آموز الزامی است');
    try {
      const allMembers = ensureArray(safeJsonParse(localStorage.getItem('teamMembers'), []));
      const filtered = allMembers.filter((m) => !idsEqual(m.studentId, studentId));
      localStorage.setItem('teamMembers', JSON.stringify(filtered));
      setMembers(filtered.filter((m) => m.status === 'active'));
      setPendingRequests(filtered.filter((m) => m.status === 'pending'));
      try {
        const existing = await teamService.getMemberByStudentAndTeam(team.id, studentId);
        if (existing) {
          await teamService.deleteMember(existing.id);
          await teamService.updateUserProfile(studentId, { teamCode: null });
        }
      } catch (e) { console.warn('⚠️ Server not available'); }
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [team, currentUserId]);

  const changeMemberRole = useCallback(async (studentId, newRole) => {
    if (!team) throw new Error('گروهی وجود ندارد');
    if (!studentId) throw new Error('شناسه دانش‌آموز الزامی است');
    try {
      const allMembers = ensureArray(safeJsonParse(localStorage.getItem('teamMembers'), []));
      const updated = allMembers.map((m) => idsEqual(m.studentId, studentId) ? { ...m, role: newRole } : m);
      localStorage.setItem('teamMembers', JSON.stringify(updated));
      setMembers(updated.filter((m) => m.status === 'active'));
      try {
        const existing = await teamService.getMemberByStudentAndTeam(team.id, studentId);
        if (existing) await teamService.updateMemberRole(existing.id, newRole);
      } catch (e) { console.warn('⚠️ Server not available'); }
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [team, currentUserId]);

  const deleteTeam = useCallback(async () => {
    if (!team) throw new Error('گروهی وجود ندارد');
    try {
      const teamId = team.id;
      try {
        const allMembers = ensureArray(await teamService.getTeamMembers(teamId));
        await Promise.all(allMembers.map((m) => teamService.deleteMember(m.id)));
        const msgs = await teamService.getChannelMessages(teamId, 1, 9999);
        const msgData = ensureArray(msgs?.data);
        if (msgData.length > 0) await teamService.deleteChannelMessagesBulk(msgData.map((m) => m.id));
        await teamService.deleteTeam(teamId);
      } catch (e) { console.warn('⚠️ Server not available'); }

      localStorage.removeItem('team');
      localStorage.removeItem('teamMembers');
      localStorage.removeItem('pendingRequests');
      localStorage.removeItem('teamCode');
      localStorage.removeItem('channelMessages');

      Object.values(reactionTimeoutRef.current).forEach(clearTimeout);
      reactionTimeoutRef.current = {};
      reactionQueueRef.current = {};

      setTeam(null); setTeamCode(null); setMembers([]); setPendingRequests([]);
      setChannelMessages([]); setPinnedMessage(null); setActivityLogs([]);
      setAllowStudentMessaging(true);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [team]);

  const respondToInvite = useCallback(async (memberId, status) => {
    if (!memberId) throw new Error('شناسه دعوت‌نامه الزامی است');
    try {
      const allMembers = ensureArray(safeJsonParse(localStorage.getItem('teamMembers'), []));
      const updated = allMembers.map((m) => m.id === memberId ? { ...m, status } : m);
      localStorage.setItem('teamMembers', JSON.stringify(updated));

      if (status === 'active') {
        const member = updated.find((m) => m.id === memberId);
        if (member) {
          const teamData = safeJsonParse(localStorage.getItem('team'), null);
          if (teamData) {
            if (teamData.allowStudentMessaging === undefined) teamData.allowStudentMessaging = true;
            setTeam(teamData);
            setAllowStudentMessaging(teamData.allowStudentMessaging);
            setTeamCode(teamData.teamCode);
          }
          await loadTeamData();
        }
      }

      setMembers(updated.filter((m) => m.status === 'active'));
      setPendingRequests(updated.filter((m) => m.status === 'pending'));

      try { await teamService.updateMemberStatus(memberId, status); }
      catch (e) { console.warn('⚠️ Server not available'); }
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [loadTeamData]);

  // ============================================================
  // ===== پیام‌های کانال =====
  // ============================================================
  const sendChannelMessage = useCallback(async (content, type = 'text', replyTo = null, pollData = null) => {
    if (!team) throw new Error('گروهی وجود ندارد');
    if (!content && type === 'text' && !pollData) throw new Error('متن پیام الزامی است');
    try {
      const newMsg = {
        id: generateId(),
        teamId: team.id,
        senderId: currentUserId,
        type,
        content: content || '',
        replyTo,
        timestamp: new Date().toISOString(),
        edited: false,
        pinned: false,
        isRead: false,
        readBy: [],
        pollData: pollData || null,
        reactions: {},
        reactionTs: {},
      };

      setChannelMessages((prev) => [...prev, newMsg]);
      const stored = ensureArray(safeJsonParse(localStorage.getItem('channelMessages'), []));
      stored.push(newMsg);
      localStorage.setItem('channelMessages', JSON.stringify(stored));

      let finalMsg = newMsg;
      try {
        const sent = await teamService.sendChannelMessage(team.id, currentUserId, content, type, replyTo, pollData);
        if (sent && sent.id) {
          finalMsg = { ...sent, reactions: {}, reactionTs: {} };
          setChannelMessages((prev) => prev.map((m) => m.id === newMsg.id ? finalMsg : m));
          const updatedStored = ensureArray(safeJsonParse(localStorage.getItem('channelMessages'), []));
          const idx = updatedStored.findIndex((m) => m.id === newMsg.id);
          if (idx !== -1) {
            updatedStored[idx] = finalMsg;
            localStorage.setItem('channelMessages', JSON.stringify(updatedStored));
          }
        }
      } catch (e) {
        console.warn('⚠️ Server not available');
      }

      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'newChannelMessage', teamId: team.id, message: finalMsg }));
      }

      return newMsg;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [team, currentUserId]);

  const editChannelMessage = useCallback(async (messageId, newContent) => {
    if (!messageId) throw new Error('شناسه پیام الزامی است');
    try {
      setChannelMessages((prev) => prev.map((m) => m.id === messageId ? { ...m, content: newContent, edited: true } : m));
      const stored = ensureArray(safeJsonParse(localStorage.getItem('channelMessages'), []));
      const updated = stored.map((m) => m.id === messageId ? { ...m, content: newContent, edited: true } : m);
      localStorage.setItem('channelMessages', JSON.stringify(updated));
      try {
        await teamService.updateChannelMessage(messageId, { content: newContent, edited: true });
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'messageEdited', channel: true, messageId, newContent }));
        }
      } catch (e) { console.warn('⚠️ Server not available'); }
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const deleteChannelMessage = useCallback(async (messageId) => {
    if (!team) throw new Error('گروهی وجود ندارد');
    if (!messageId) throw new Error('شناسه پیام الزامی است');
    try {
      setChannelMessages((prev) => prev.filter((m) => m.id !== messageId));
      const stored = ensureArray(safeJsonParse(localStorage.getItem('channelMessages'), []));
      localStorage.setItem('channelMessages', JSON.stringify(stored.filter((m) => m.id !== messageId)));
      try {
        const rStore = safeJsonParse(localStorage.getItem('messageReactions'), {});
        delete ensureObject(rStore)[messageId];
        localStorage.setItem('messageReactions', JSON.stringify(rStore));
        const tStore = safeJsonParse(localStorage.getItem('messageReactionTs'), {});
        delete ensureObject(tStore)[messageId];
        localStorage.setItem('messageReactionTs', JSON.stringify(tStore));
      } catch {}
      try {
        await teamService.deleteChannelMessage(messageId);
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'messageDeleted', channel: true, messageId }));
        }
      } catch (e) { console.warn('⚠️ Server not available'); }
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [team]);

  const pinChannelMessage = useCallback(async (messageId, pin = true) => {
  if (!messageId) throw new Error('شناسه پیام الزامی است');
  try {
    setChannelMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, pinned: pin } : m))
    );

    const stored = ensureArray(safeJsonParse(localStorage.getItem('channelMessages'), []));
    const updated = stored.map((m) =>
      m.id === messageId ? { ...m, pinned: pin } : m
    );
    localStorage.setItem('channelMessages', JSON.stringify(updated));

    // ✅ آپدیت هر دو لیست
    const pinnedList = updated.filter((m) => m.pinned === true);
    setPinnedMessages(pinnedList);
    setPinnedMessage(pinnedList[0] || null);

    try {
      await teamService.pinChannelMessage(messageId, pin);
      await teamService.addActivityLog(
        team?.id, currentUserId,
        pin ? 'pin_message' : 'unpin_message',
        `پیام ${pin ? 'پین' : 'آنپین'} شد`
      );
    } catch (e) { console.warn('⚠️ Server not available'); }
  } catch (err) {
    setError(err.message);
    throw err;
  }
}, [team, currentUserId]);


  const loadMoreChannelMessages = useCallback(async (page) => {
  if (!team) return;
  try {
    const res = await teamService.getChannelMessages(team.id, page, 50);
    const rawData = ensureArray(res?.data);
    const total = typeof res?.total === 'number' ? res.total : rawData.length;

    const sanitized = sanitizeRawMessages(rawData);
    const hydrated = hydrateMessagesWithLocalReactions(sanitized);

    // ✅ پیام‌های قدیمی‌تر رو به ابتدای لیست اضافه کن (نه انتها)
    setChannelMessages((prev) => {
      const existingIds = new Set(prev.map((m) => m.id));
      const olderOnes = hydrated.filter((m) => !existingIds.has(m.id));
      return [...olderOnes, ...prev]; // ✅ prepend به جای append
    });
    setChannelTotal(total);
    setChannelPage(page);
    channelPageRef.current = page;

    // localStorage هم به همان شکل
    const stored = ensureArray(safeJsonParse(localStorage.getItem('channelMessages'), []));
    const storedIds = new Set(stored.map((m) => m.id));
    const olderToAdd = hydrated.filter((m) => !storedIds.has(m.id));
    localStorage.setItem('channelMessages', JSON.stringify([...olderToAdd, ...stored]));
  } catch (err) {
    setError(err.message);
  }
}, [team, hydrateMessagesWithLocalReactions, sanitizeRawMessages]);

  // ============================================================
  // ===== نظرسنجی =====
  // ============================================================
  const createPoll = useCallback(async (question, options, config = {}) => {
  if (!team) throw new Error('گروهی وجود ندارد');
  if (!question || question.trim() === '') throw new Error('سؤال الزامی است');
  if (!options || options.length < 2) throw new Error('حداقل ۲ گزینه لازم است');
  if (options.length > 12) throw new Error('حداکثر ۱۲ گزینه مجاز است');
  try {
    // ✅ محاسبه‌ی زمان انقضا
    let expiresAt = null;
    if (config.duration && config.duration.value > 0) {
      const unitMs =
        config.duration.unit === 'second' ? 1000 :
        config.duration.unit === 'minute' ? 60 * 1000 :
        config.duration.unit === 'hour'   ? 60 * 60 * 1000 :
        config.duration.unit === 'day'    ? 24 * 60 * 60 * 1000 :
        60 * 1000;
      expiresAt = Date.now() + config.duration.value * unitMs;
    }

    const pollData = {
      question: question.trim(),
      options: options.map((opt) => ({ text: opt.trim(), votes: 0, voterNames: [] })),
      type: config.type || 'single',
      anonymous: !!config.anonymous,
      isQuiz: !!config.isQuiz,
      correctOption: config.isQuiz ? config.correctOption : undefined,
      duration: config.duration || null,
      expiresAt: expiresAt,                              // ✅ جدید
      hideResults: !!config.hideResults,
      shuffleOptions: !!config.shuffleOptions,
      allowChangeVote: config.allowChangeVote !== undefined ? config.allowChangeVote : true,
      allowSuggest: !!config.allowSuggest,
      description: config.description || '',
      isClosed: false, totalVotes: 0, votedBy: [], userVotes: {},
      createdAt: new Date().toISOString(), closedAt: null,
    };
    return await sendChannelMessage('', 'poll', null, pollData);
  } catch (err) {
    setError(err.message);
    throw err;
  }
}, [team, sendChannelMessage]);

  const votePoll = useCallback(async (pollId, optionIndex) => {
  if (!pollId || currentUserId === undefined) throw new Error('شناسه نظرسنجی و کاربر الزامی است');
  try {
    await teamService.votePoll(pollId, currentUserId, optionIndex);
    const updatedMsg = await teamService.getChannelMessage(pollId);
    if (updatedMsg) {
      setChannelMessages((prev) =>
        prev.map((msg) => (msg.id === pollId ? { ...updatedMsg, readBy: updatedMsg.readBy || [] } : msg))
      );
      const stored = ensureArray(safeJsonParse(localStorage.getItem('channelMessages'), []));
      const idx = stored.findIndex((m) => m.id === pollId);
      if (idx !== -1) {
        stored[idx] = updatedMsg;
        localStorage.setItem('channelMessages', JSON.stringify(stored));
      }

      // ✅ broadcast به بقیه
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'pollUpdated',
          teamId: team?.id,
          messageId: pollId,
          pollData: updatedMsg.pollData,
        }));
      }
    }
    return true;
  } catch (err) {
    setError(err.message);
    throw err;
  }
}, [currentUserId, team?.id]);

  const closePoll = useCallback(async (pollId) => {
  if (!pollId) throw new Error('شناسه نظرسنجی الزامی است');
  try {
    await teamService.closePoll(pollId);
    const updatedMsg = await teamService.getChannelMessage(pollId);
    if (updatedMsg) {
      setChannelMessages((prev) =>
        prev.map((msg) => (msg.id === pollId ? { ...updatedMsg, readBy: updatedMsg.readBy || [] } : msg))
      );
      const stored = ensureArray(safeJsonParse(localStorage.getItem('channelMessages'), []));
      const idx = stored.findIndex((m) => m.id === pollId);
      if (idx !== -1) {
        stored[idx] = updatedMsg;
        localStorage.setItem('channelMessages', JSON.stringify(stored));
      }

      // ✅ broadcast
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'pollUpdated',
          teamId: team?.id,
          messageId: pollId,
          pollData: updatedMsg.pollData,
        }));
      }
    }
    return true;
  } catch (err) {
    setError(err.message);
    throw err;
  }
}, [team?.id]);

  const deletePoll = useCallback(async (pollId) => {
    if (!pollId) throw new Error('شناسه الزامی است');
    try {
      await teamService.deletePoll(pollId);
      setChannelMessages((prev) => prev.filter((msg) => msg.id !== pollId));
      const stored = ensureArray(safeJsonParse(localStorage.getItem('channelMessages'), []));
      localStorage.setItem('channelMessages', JSON.stringify(stored.filter((m) => m.id !== pollId)));
      await teamService.addActivityLog(team?.id, currentUserId, 'delete_poll', `نظرسنجی حذف شد`);
      return true;
    } catch (err) { setError(err.message); throw err; }
  }, [team, currentUserId]);

  const pinPoll = useCallback(async (pollId) => {
    if (!pollId) throw new Error('شناسه الزامی است');
    try {
      const currentPinned = channelMessages.find((m) => m.pinned === true);
      if (currentPinned) await teamService.pinChannelMessage(currentPinned.id, false);
      await teamService.pinPoll(pollId);
      const updatedMsg = await teamService.getChannelMessage(pollId);
      if (updatedMsg) {
        setChannelMessages((prev) => prev.map((msg) => msg.id === pollId ? { ...updatedMsg, readBy: updatedMsg.readBy || [] } : msg));
        const stored = ensureArray(safeJsonParse(localStorage.getItem('channelMessages'), []));
        const idx = stored.findIndex((m) => m.id === pollId);
        if (idx !== -1) { stored[idx] = updatedMsg; localStorage.setItem('channelMessages', JSON.stringify(stored)); }
        setPinnedMessage(updatedMsg);
      }
      await teamService.addActivityLog(team?.id, currentUserId, 'pin_poll', `نظرسنجی پین شد`);
      return true;
    } catch (err) { setError(err.message); throw err; }
  }, [team, currentUserId, channelMessages]);

  const suggestPollOption = useCallback(async (pollId, newOptionText) => {
    if (!pollId || !currentUserId) throw new Error('شناسه الزامی است');
    try {
      await teamService.suggestPollOption(pollId, currentUserId, newOptionText);
      const updatedMsg = await teamService.getChannelMessage(pollId);
      if (updatedMsg) {
        setChannelMessages((prev) => prev.map((msg) => msg.id === pollId ? { ...updatedMsg, readBy: updatedMsg.readBy || [] } : msg));
        const stored = ensureArray(safeJsonParse(localStorage.getItem('channelMessages'), []));
        const idx = stored.findIndex((m) => m.id === pollId);
        if (idx !== -1) { stored[idx] = updatedMsg; localStorage.setItem('channelMessages', JSON.stringify(stored)); }
      }
      return true;
    } catch (err) { setError(err.message); throw err; }
  }, [currentUserId]);

  const getPollResults = useCallback(async (pollId) => {
    if (!pollId) throw new Error('شناسه الزامی است');
    try { return await teamService.getPollResults(pollId); }
    catch (err) { setError(err.message); throw err; }
  }, []);

  // ============================================================
  // ===== searchUser =====
  // ============================================================
  const searchUser = useCallback(async (phone) => {
    if (!phone || phone.trim() === '') throw new Error('شماره موبایل را وارد کنید');
    const normalizedInput = normalizePhone(phone);
    let users = ensureArray(safeJsonParse(localStorage.getItem('users'), []));
    if (users.length === 0) {
      try {
        const response = await fetch(`${API_BASE}/users`);
        if (response.ok) {
          users = ensureArray(await response.json());
          localStorage.setItem('users', JSON.stringify(users));
        }
      } catch (e) { /* ignore */ }
    }
    const found = users.find((u) => normalizePhone(u.phone) === normalizedInput);
    if (found) return found;

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);
      const response = await fetch(`${API_BASE}/users`, { signal: controller.signal });
      clearTimeout(timeoutId);
      if (response.ok) {
        const allUsers = ensureArray(await response.json());
        const user = allUsers.find((u) => normalizePhone(u.phone) === normalizedInput);
        if (user) {
          const stored = ensureArray(safeJsonParse(localStorage.getItem('users'), []));
          if (!stored.find((u) => idsEqual(u.id, user.id))) {
            stored.push(user);
            localStorage.setItem('users', JSON.stringify(stored));
          }
          return user;
        }
      }
    } catch (err) { console.warn('سرور در دسترس نیست'); }
    return null;
  }, []);

  // ============================================================
  // ===== getUserName =====
  // ============================================================
  const getUserName = useCallback((userId) => {
    if (team?.teacherId && idsEqual(userId, team.teacherId)) {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const u = JSON.parse(storedUser);
          if (u.firstName && u.lastName) return `${u.firstName} ${u.lastName}`.trim();
          if (u.name) return u.name;
          if (u.username) return u.username;
        } catch (e) {}
      }
      const teacherMember = members.find((m) => idsEqual(m.studentId, userId));
      if (teacherMember?.name) return teacherMember.name;
      return 'معلم';
    }
    const member = members.find((m) => idsEqual(m.studentId, userId));
    if (member?.name) return member.name;
    const storedUsers = localStorage.getItem('users');
    if (storedUsers) {
      try {
        const users = ensureArray(JSON.parse(storedUsers));
        const u = users.find((x) => idsEqual(x.id, userId));
        if (u) {
          if (u.firstName && u.lastName) return `${u.firstName} ${u.lastName}`.trim();
          if (u.name) return u.name;
          if (u.fullName) return u.fullName;
          if (u.username) return u.username;
          if (u.firstName) return u.firstName;
        }
      } catch (e) {}
    }
    return `کاربر ${userId}`;
  }, [team, members]);

  // ============================================================
  // ===== exportChat =====
  // ============================================================
  const exportChat = useCallback((messages, format = 'txt') => {
    const safeMessages = ensureArray(messages);
    if (safeMessages.length === 0) { alert('هیچ پیامی وجود ندارد'); return; }
    const text = safeMessages.map((m) => `${new Date(m.timestamp).toLocaleString('fa-IR')} - ${m.senderId}: ${m.content}`).join('\n');
    const timestamp = new Date().toISOString().slice(0, 10);
    if (format === 'txt') {
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `chat_${timestamp}.txt`; a.click();
      URL.revokeObjectURL(url);
    } else if (format === 'json') {
      const json = JSON.stringify(safeMessages, null, 2);
      const blob = new Blob([json], { type: 'application/json;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `chat_${timestamp}.json`; a.click();
      URL.revokeObjectURL(url);
    } else alert('فرمت پشتیبانی نمی‌شود');
  }, []);

  // ============================================================
  // ===== سیستم ری‌اکشن =====
  // ============================================================
  const syncReactionToServer = useCallback(async (messageId) => {
    const localMsg = channelMessagesRef.current.find((m) => m.id === messageId);
    if (!localMsg) return;
    const localR = normalizeReactions(localMsg.reactions);
    const localTs = normalizeReactionTs(localMsg.reactionTs);
    try {
      let serverMsg = null;
      try { serverMsg = await teamService.getChannelMessage(messageId); } catch (e) { return; }
      let mergedR = localR, mergedTs = localTs;
      if (serverMsg) {
        const serverR = normalizeReactions(serverMsg.reactions);
        const serverTs = normalizeReactionTs(serverMsg.reactionTs);
        const merged = mergeReactionMaps(localR, localTs, serverR, serverTs);
        mergedR = merged.reactions; mergedTs = merged.reactionTs;
      }
      const serverRNorm = normalizeReactions(serverMsg?.reactions || {});
      const serverTsNorm = normalizeReactionTs(serverMsg?.reactionTs || {});
      const localHasExtra = Object.keys(localR).some((uid) => !(uid in serverRNorm) || (localTs[uid] || 0) > (serverTsNorm[uid] || 0));
      const serverHasExtra = Object.keys(serverRNorm).some((uid) => !(uid in localR));
      if (!localHasExtra && !serverHasExtra) return;
      if (!localHasExtra && serverHasExtra) {
        applyReactionsToState(messageId, serverRNorm, serverTsNorm);
        persistReactionsToLocal(messageId, serverRNorm, serverTsNorm);
        return;
      }
      const saved = await teamService.updateChannelMessage(messageId, { reactions: mergedR, reactionTs: mergedTs });
      if (saved) {
        const savedR = normalizeReactions(saved.reactions);
        const savedTs = normalizeReactionTs(saved.reactionTs);
        const latestLocal = channelMessagesRef.current.find((m) => m.id === messageId);
        if (latestLocal) {
          const nowR = normalizeReactions(latestLocal.reactions);
          const nowTs = normalizeReactionTs(latestLocal.reactionTs);
          const final = mergeReactionMaps(nowR, nowTs, savedR, savedTs);
          if (JSON.stringify(final.reactions) !== JSON.stringify(nowR) || JSON.stringify(final.reactionTs) !== JSON.stringify(nowTs)) {
            applyReactionsToState(messageId, final.reactions, final.reactionTs);
            persistReactionsToLocal(messageId, final.reactions, final.reactionTs);
          }
        }
      }
    } catch (e) { console.warn('⚠️ Reaction sync failed:', e); }
  }, [applyReactionsToState, persistReactionsToLocal]);

  const enqueueReactionSync = useCallback((messageId) => {
    const prev = reactionQueueRef.current[messageId] || Promise.resolve();
    const next = prev.catch(() => {}).then(() => syncReactionToServer(messageId));
    reactionQueueRef.current[messageId] = next;
    next.finally(() => {
      if (reactionQueueRef.current[messageId] === next) delete reactionQueueRef.current[messageId];
    });
    return next;
  }, [syncReactionToServer]);

  const scheduleReactionSync = useCallback((messageId) => {
    if (reactionTimeoutRef.current[messageId]) clearTimeout(reactionTimeoutRef.current[messageId]);
    reactionTimeoutRef.current[messageId] = setTimeout(() => {
      delete reactionTimeoutRef.current[messageId];
      enqueueReactionSync(messageId);
    }, REACTION_DEBOUNCE_MS);
  }, [enqueueReactionSync]);

  const sendReaction = useCallback((messageId, emoji) => {
    if (!messageId || !emoji || !currentUserId) return;
    const currentMsg = channelMessagesRef.current.find((m) => m.id === messageId);
    if (!currentMsg) return;

    const now = Date.now();
    const currentR = normalizeReactions(currentMsg.reactions);
    const currentTs = normalizeReactionTs(currentMsg.reactionTs);
    const wasReacted = currentR[currentUserId] === emoji;
    const newR = { ...currentR };
    const newTs = { ...currentTs };

    if (wasReacted) { newR[currentUserId] = ''; newTs[currentUserId] = now; }
    else { newR[currentUserId] = emoji; newTs[currentUserId] = now; }

    applyReactionsToState(messageId, newR, newTs);
    persistReactionsToLocal(messageId, newR, newTs);

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'reaction', messageId, userId: currentUserId,
        emoji: wasReacted ? null : emoji, ts: now,
      }));
    }
    scheduleReactionSync(messageId);
  }, [currentUserId, applyReactionsToState, persistReactionsToLocal, scheduleReactionSync]);

  const getMessageReactions = useCallback((messageId) => {
    const msg = channelMessagesRef.current.find((m) => m.id === messageId);
    return normalizeReactions(msg?.reactions);
  }, []);

  // ============================================================
  // ===== توابع teamCode =====
  // ============================================================
  const getTeamCode = useCallback(() => teamCode || localStorage.getItem('teamCode'), [teamCode]);

  const getUserTeamCode = useCallback(async (userId) => {
    if (!userId) return null;
    try {
      const storedUsers = localStorage.getItem('users');
      if (storedUsers) {
        const users = ensureArray(JSON.parse(storedUsers));
        const user = users.find((u) => idsEqual(u.id, userId));
        if (user?.teamCode) return user.teamCode;
      }
      const user = await teamService.getUserById(userId);
      if (user?.teamCode) {
        const users = ensureArray(safeJsonParse(localStorage.getItem('users'), []));
        const idx = users.findIndex((u) => idsEqual(u.id, userId));
        if (idx !== -1) users[idx].teamCode = user.teamCode;
        else users.push({ id: userId, teamCode: user.teamCode });
        localStorage.setItem('users', JSON.stringify(users));
        return user.teamCode;
      }
      return null;
    } catch (err) { console.error('❌ Error:', err); return null; }
  }, []);

  const updateUserTeamCode = useCallback(async (userId, newTeamCode) => {
    if (!userId) return false;
    try {
      const users = ensureArray(safeJsonParse(localStorage.getItem('users'), []));
      const idx = users.findIndex((u) => idsEqual(u.id, userId));
      if (idx !== -1) users[idx].teamCode = newTeamCode;
      else users.push({ id: userId, teamCode: newTeamCode });
      localStorage.setItem('users', JSON.stringify(users));
      try { await teamService.updateUserProfile(userId, { teamCode: newTeamCode }); return true; }
      catch (e) { return true; }
    } catch (err) { setError(err.message); return false; }
  }, []);

  // ============================================================
  // ===== WebSocket =====
  // ============================================================
  useEffect(() => {
    if (!currentUserId || !team?.id) return;
    let isActive = true;
    let localWs = null;

    const setupWebSocket = () => {
      if (!isActive) return;

      const onMessage = async (data) => {
        if (!isActive) return;
        if (data.type === 'newChannelMessage' && idsEqual(data.teamId, team?.id)) {
          const msg = data.message;
          if (msg && msg.id) {
            setChannelMessages((prev) => {
              if (prev.find((m) => m.id === msg.id)) return prev;
              return [...prev, { reactions: {}, reactionTs: {}, ...msg }];
            });
            const stored = ensureArray(safeJsonParse(localStorage.getItem('channelMessages'), []));
            if (!stored.find((m) => m.id === msg.id)) {
              stored.push(msg);
              localStorage.setItem('channelMessages', JSON.stringify(stored));
            }
          }
        } else if (data.type === 'typing' && idsEqual(data.teamId, team?.id)) {
          if (!idsEqual(data.userId, currentUserId)) {
            setIsTyping(true);
            setTypingUser(data.userName || `کاربر ${data.userId}`);
            if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
            typingTimeoutRef.current = setTimeout(() => { setIsTyping(false); setTypingUser(null); }, 3000);
          }
        } else if (data.type === 'messageEdited' && data.channel) {
          setChannelMessages((prev) => prev.map((m) => (m.id === data.messageId ? { ...m, content: data.newContent, edited: true } : m)));
        } else if (data.type === 'messageDeleted' && data.channel) {
          setChannelMessages((prev) => prev.filter((m) => m.id !== data.messageId));
        } else if (data.type === 'reaction') {
          const { messageId, userId, emoji, ts, reactions: senderR, reactionTs: senderTs } = data;
          if (!messageId) return;
          let deltaR = {}, deltaTs = {};
          if (senderR && senderTs) { deltaR = normalizeReactions(senderR); deltaTs = normalizeReactionTs(senderTs); }
          else if (userId !== undefined) {
            const useTs = ts || Date.now();
            if (emoji) { deltaR[userId] = emoji; deltaTs[userId] = useTs; }
            else { deltaR[userId] = null; deltaTs[userId] = useTs; }
          } else return;

          const m = channelMessagesRef.current.find((msg) => msg.id === messageId);
          if (!m) return;
          const localR = normalizeReactions(m.reactions);
          const localTs = normalizeReactionTs(m.reactionTs);
          const merged = mergeReactionMaps(localR, localTs, deltaR, deltaTs);
          const updatedMsg = { ...m, reactions: merged.reactions, reactionTs: merged.reactionTs };
          setChannelMessages((prev) => prev.map((x) => (x.id === messageId ? updatedMsg : x)));
          persistReactionsToLocal(messageId, merged.reactions, merged.reactionTs);
        } else if (data.type === 'messagesRead' && !idsEqual(data.userId, currentUserId)) {
          setChannelMessages((prev) => prev.map((m) =>
            data.messageIds?.includes(m.id) && !ensureArray(m.readBy).some((id) => idsEqual(id, data.userId))
              ? { ...m, readBy: [...ensureArray(m.readBy), data.userId] }
              : m
          ));
        } else if (data.type === 'teamUpdated' && idsEqual(data.teamId, team?.id)) {
          setTeam((prev) => {
            const updated = { ...(prev || {}), ...data.updates };
            localStorage.setItem('team', JSON.stringify(updated));
            if (updated.allowStudentMessaging !== undefined) setAllowStudentMessaging(updated.allowStudentMessaging);
            return updated;
          });
        } else if (data.type === 'pollUpdated' && idsEqual(data.teamId, team?.id)) {
  console.log('📊 Poll updated from server:', data.messageId);
  setChannelMessages((prev) =>
    prev.map((m) => m.id === data.messageId ? { ...m, pollData: data.pollData } : m)
  );
  // ✅ localStorage
  const stored = ensureArray(safeJsonParse(localStorage.getItem('channelMessages'), []));
  const idx = stored.findIndex((m) => m.id === data.messageId);
  if (idx !== -1) {
    stored[idx] = { ...stored[idx], pollData: data.pollData };
    localStorage.setItem('channelMessages', JSON.stringify(stored));
  }
}  else if (data.type === 'presence') {
  const { userId, ts } = data;
  if (!userId) return;
  setPresenceMap((prev) => ({ ...prev, [userId]: ts || Date.now() }));

  // ✅ آپدیت localStorage
  try {
    const users = ensureArray(safeJsonParse(localStorage.getItem('users'), []));
    const idx = users.findIndex((u) => u && idsEqual(u.id, userId));
    if (idx !== -1) {
      users[idx] = { ...users[idx], lastSeen: ts || Date.now() };
    } else {
      users.push({ id: userId, lastSeen: ts || Date.now() });
    }
    localStorage.setItem('users', JSON.stringify(users));
  } catch (e) { /* ignore */ }
}
      };

      const onOpen = () => { if (isActive) { console.log('✅ WebSocket connected'); reconnectAttemptRef.current = 0; } };
      const onClose = () => {
        if (!isActive) return;
        if (reconnectAttemptRef.current >= WS_MAX_RETRIES) return;
        if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
        const delay = getBackoffDelay(reconnectAttemptRef.current);
        reconnectAttemptRef.current++;
        reconnectTimeoutRef.current = setTimeout(() => { if (isActive) setupWebSocket(); }, delay);
      };
      const onError = () => {};

      try {
        localWs = teamService.createWebSocketConnection(currentUserId, onMessage, onOpen, onClose, onError);
        wsRef.current = localWs;
      } catch (e) { console.warn('⚠️ WebSocket not available'); }
    };

    setupWebSocket();

    return () => {
      isActive = false;
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      if (localWs) { try { localWs.close(); } catch (e) {} }
      if (wsRef.current === localWs) wsRef.current = null;
    };
  }, [currentUserId, team?.id]);

  // 🆕 پاکسازی دوره‌ای درخواست‌های منقضی (هر ۳۰ ثانیه)
  const expireIntervalRef = useRef(null);
  useEffect(() => {
    if (!team?.id) return;
    if (joinMode !== 'invite_only') return;

    const tick = () => {
      teamService.expireJoinRequests(team.id).then((r) => {
        if (r?.cleaned > 0) {
          setTimeout(() => refreshTeam(), 300);
        }
      }).catch(() => { /* ignore */ });
    };

    // اولین اجرا با تأخیر کوچک
    const initialTimer = setTimeout(tick, 2000);

    expireIntervalRef.current = setInterval(tick, 30000);

    const onVisible = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearTimeout(initialTimer);
      if (expireIntervalRef.current) {
        clearInterval(expireIntervalRef.current);
        expireIntervalRef.current = null;
      }
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [team?.id, joinMode, refreshTeam]);

  // ============================================================
  // ===== Cleanup =====
  // ============================================================
  useEffect(() => {
    return () => {
      Object.values(reactionTimeoutRef.current).forEach(clearTimeout);
      reactionTimeoutRef.current = {};
      reactionQueueRef.current = {};
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (cacheRetryTimeoutRef.current) clearTimeout(cacheRetryTimeoutRef.current);
      if (emptyRetryTimeoutRef.current) clearTimeout(emptyRetryTimeoutRef.current);
      if (expireIntervalRef.current) clearInterval(expireIntervalRef.current);
    };
  }, []);

  // ============================================================
  // 🆕 درخواست عضویت + پاسخ به درخواست
  // ============================================================
  const requestJoinToTeam = useCallback(async (targetTeamId) => {
    if (!currentUserId) throw new Error('ابتدا وارد شوید');
    if (!targetTeamId) throw new Error('شناسه گروه الزامی است');

    const result = await teamService.requestJoin(targetTeamId, currentUserId);

    // بعد از موفقیت، درخواست‌های منقضی را هم پاک کن
    if (result?.status === 'pending') {
      // refresh در پس‌زمینه
      setTimeout(() => {
        try { refreshTeam(); } catch { /* ignore */ }
      }, 200);
    }

    return result;
  }, [currentUserId, refreshTeam]);

  const respondToJoinRequest = useCallback(async (messageId, action) => {
    if (!currentUserId) throw new Error('ابتدا وارد شوید');
    if (!team?.id) throw new Error('گروه‌ای یافت نشد');

    // نام پاسخ‌دهنده
    let responderName = 'مدیر';
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const u = JSON.parse(storedUser);
        if (u.firstName && u.lastName) responderName = `${u.firstName} ${u.lastName}`.trim();
        else if (u.name) responderName = u.name;
        else if (u.fullName) responderName = u.fullName;
        else if (u.username) responderName = u.username;
      }
    } catch { /* ignore */ }

    const result = await teamService.respondToJoinRequest(
      messageId,
      action,
      currentUserId,
      responderName
    );

    // refresh برای بازتاب تغییرات
    setTimeout(() => {
      try { refreshTeam(); } catch { /* ignore */ }
    }, 150);

    return result;
  }, [currentUserId, team?.id, refreshTeam]);

  // 🆕 آیا کاربر می‌تواند به درخواست پاسخ دهد؟
  const canRespondToJoinRequest = useCallback((userIdToCheck) => {
    if (!userIdToCheck || !team) return false;
    if (currentUserRole === 'teacher') return true;
    const m = members.find((x) => x && idsEqual(x.studentId, userIdToCheck));
    if (!m) return false;
    if (m.role === 'admin' && representativeCanApproveRequests) return true;
    return false;
  }, [currentUserRole, members, team, representativeCanApproveRequests]);

  // ============================================================
  // ===== خروجی نهایی =====
  // ============================================================
  return {
    team, members, pendingRequests, channelMessages, loading, error,
    pinnedMessage, activityLogs, unreadCounts, isTyping, typingUser,
    messageReactions, setMessageReactions, teamCode, allowStudentMessaging,
    setAllowStudentMessaging,
    refreshTeam, createNewTeam, updateTeamInfo, addMember, removeMember,
    changeMemberRole, deleteTeam, respondToInvite,
    sendChannelMessage, editChannelMessage, deleteChannelMessage,
    pinChannelMessage, loadMoreChannelMessages, channelPage, channelTotal, setChannelPage,
    createPoll, votePoll, closePoll, deletePoll, pinPoll, suggestPollOption, getPollResults,
    searchUser, exportChat,
    sendReaction, markMessagesAsRead, getMessageReactions,
    getTeamCode, getUserTeamCode, updateUserTeamCode,
    pinnedMessages, presenceMap,
    // 🆕
    joinMode,
    representativeCanApproveRequests,
    canRespondToJoinRequest,
    requestJoinToTeam,
    respondToJoinRequest,
  };
};

// ============================================================
// ===== Debug Helpers =====
// ============================================================
if (typeof window !== 'undefined') {
  window.__teamDebug = () => {
    const user = safeJsonParse(localStorage.getItem('user'), null);
    const team = safeJsonParse(localStorage.getItem('team'), null);
    const members = ensureArray(safeJsonParse(localStorage.getItem('teamMembers'), []));
    const messages = ensureArray(safeJsonParse(localStorage.getItem('channelMessages'), []));
    console.group('🔍 === Team Debug ===');
    console.log('👤 user:', user);
    console.log('🏫 team:', team);
    console.log('🔑 teamCode:', localStorage.getItem('teamCode'));
    console.log('👥 members:', members.length);
    console.log('💬 messages:', messages.length);
    if (messages.length > 0) {
      console.log('  last 3:', messages.slice(-3).map((m) => ({ id: m.id, sender: m.senderId, content: (m.content || '').slice(0, 30) })));
    }
    console.log('❤️ reactions:', safeJsonParse(localStorage.getItem('messageReactions'), {}));
    console.groupEnd();
  };

  window.__teamForceSync = () => {
    console.log('🔄 Force sync...');
    localStorage.removeItem('team');
    localStorage.removeItem('teamMembers');
    localStorage.removeItem('channelMessages');
    localStorage.removeItem('teamCode');
    localStorage.removeItem('messageReactions');
    localStorage.removeItem('messageReactionTs');
    setTimeout(() => window.location.reload(), 300);
  };

  window.__teamCheckServer = async () => {
    console.group('🌐 === Server Check ===');
    try {
      const teams = await (await fetch(`${API_BASE}/teams`)).json();
      console.log('🏫 teams:', Array.isArray(teams) ? teams.length : teams);
      const members = await (await fetch(`${API_BASE}/teamMembers`)).json();
      console.log('👥 members:', Array.isArray(members) ? members.length : members);
      const msgs = await (await fetch(`${API_BASE}/channelMessages`)).json();
      console.log('💬 messages:', Array.isArray(msgs) ? msgs.length : msgs);
    } catch (e) { console.error('❌', e); }
    console.groupEnd();
  };

  console.log('💡 Debug: __teamDebug() | __teamForceSync() | __teamCheckServer()');
}